import { getTwilioTokenApi, sendTwilioSMS, sendTwilioWhatsApp } from "../apis/twilio";
// eslint-disable-next-line no-unused-vars
import { ReplaySubject, Observable } from "rxjs";
import { LocalStorageService } from "./localStorageService";
import socketService from "./socketService";
import toastr from "toastr";
import { TRAVELASSIST_ZENDESK_TICKET_ENDPOINT } from "../constants/endpoints";
import { CredentialsService } from "./credentialsService";

// eslint-disable-next-line no-unused-vars
const { Device } = require("@twilio/voice-sdk");
const TOKEN_EXPIRED_CODE = 31205;
const SMS_NOT_VALID_NUMBER_ERROR = 21211;
const SMS_NOT_ENABLED_REGION_ERROR = 21408;
const WHATSAPP_SESSION_EXPIRED_ERROR = "SESSION_EXPIRED";
const WHATSAPP_NOT_VALID_NUMBER_ERROR = String(SMS_NOT_VALID_NUMBER_ERROR);
const NO_DEVICE_FOUND = 31201;
const JWT_TOKEN_PARSING_FAILED = 31204;

let triedLoadingWithoutToken = false;
const _twilioStatus = new ReplaySubject(0);
const _callRinging = new ReplaySubject(0);
const _callRingingStopped = new ReplaySubject(0);
const _callStarted = new ReplaySubject(0);
const _callStopped = new ReplaySubject(0);
/**
 * @class TwilioService
 *
 * https://www.twilio.com/docs/voice/client/javascript/connection
 */
class TwilioService {
  constructor() {
    this.twilioStatus = false;
    _twilioStatus.next(false);
    /**
     * @type {string}
     * @description token used to identify the client, it is fetched from the API
     */
    this.token = null;
    /**
     * @type {Device}
     * @description Twilio Device instance
     */
    this.device = null;
    /**
     * @type {Observable<boolean>}
     * @description emits the status changes of twilio
     */
    this.statusObservable = _twilioStatus.asObservable();
    this.statusObservable.subscribe((status) => (this.twilioStatus = status));
    /**
     * @type {Observable<{connection:Connection,isIncoming:boolean}>}
     * @description emits event phone is ringing, if isIncoming is set it is incoming call and it can be answered by calling accept() or rejected by calling reject() on connection object
     */
    this.callRingingObservable = _callRinging.asObservable();
    /**
     * @type {Observable<{connection:Connection,accepted:boolean,isIncoming:boolean}>}
     * @description emits when phone stops ringing
     */
    this.callRingingStoppedObservable = _callRingingStopped.asObservable();
    /**
     * @type {Observable<{connection:Connection,isIncoming:boolean}>}
     * @description emits event when inbound/outbound call is started
     */
    this.callStartedObservable = _callStarted.asObservable();
    /**
     * @type {Observable<Connection>}
     * @description emits event when inbound/outbound call is finished by anyone
     */
    this.callStoppedObservable = _callStopped.asObservable();
  }

  /**
   * Starts all the listeners and creates Device instance with loaded token
   */
  start() {
    if (!CredentialsService.isSimulation()) {
      // If token is still not fethed set flag to initialize when it loads
      if (!this.token) {
        triedLoadingWithoutToken = true;
      } else {
        this.device = new Device(this.token, {
          codecPreferences: ["opus", "pcmu"],
          logLevel: 1,
        });
        this.initializeListeners();
        this.device.register();
        socketService.sendEvent("twilio-start");
      }
    }
  }

  stop() {
    if (this.device) {
      this.device.destroy();
      this.device = null;
    }
    this.token = null;
    triedLoadingWithoutToken = false;
  }

  async loadToken() {
    const {
      data: { token },
    } = await getTwilioTokenApi();

    this.token = token;
    if (triedLoadingWithoutToken) {
      this.start();
    }
  }

  clearToken() {
    this.token = null;
  }

  initializeListeners() {
    if (!this.device) return;

    this.device.on("registered", () => {
      console.log("Twilio is ready");
      _twilioStatus.next(true);
    });

    this.device.on("error", (error) => {
      console.log("Twilio error happened");
      console.log(error);
      switch (error.code) {
        case TOKEN_EXPIRED_CODE:
          this.stop();
          triedLoadingWithoutToken = true;
          this.loadToken();
          break;
        case NO_DEVICE_FOUND:
          toastr.error("No voice device found!", "Call failed!");
          break;
        case JWT_TOKEN_PARSING_FAILED:
          break;
        default:
          toastr.error(error.message, "Twilio call error");
          break;
      }
    });

    this.device.on("incoming", (call) => {
      this.setConnectionListeners(call, false);
    });
  }

  /**
   * Called to initiate call with given phone number
   *
   * @param {string} phoneNumber
   * @param {{
   *   zendeskId?:string,
   *   forceNoTicket?:boolean,
   *   product:string,
   *   zendeskInstance?:string
   * }} applicantInfo if its clients number
   */
  async startCall(phoneNumber, applicantInfo = {}) {
    try {
      const isNotReady = !["registered", "registering"].includes(this.deviceStatus);
      if (isNotReady) {
        if (!this.token) {
          await this.loadToken();
        }
        this.start();
      }
      if (this.deviceStatus !== "registered") {
        await new Promise((resolve) => {
          let timeoutId;
          const sub = this.statusObservable.subscribe((status) => {
            if (status) {
              clearTimeout(timeoutId);
              sub.unsubscribe();
              resolve();
            }
          });
          timeoutId = setTimeout(() => { sub.unsubscribe(); resolve(); }, 5000);
        });
      }
    } catch (error) {
      console.error("Failed to initialize Twilio:", error);
      toastr.error("Failed to initialize Twilio. Please try again.", "Call failed");
      return;
    }
    if (this.deviceStatus === "registered") {
      try {
        const call = await this.device.connect({
          params: {
            phoneNumber,
            token: LocalStorageService.getToken(),
            ...applicantInfo,
          },
        });

        if (call) {
          this.setConnectionListeners(call, true);
        }
      } catch (error) {
        console.error("Failed to connect call:", error);
        toastr.error(error.message || "Failed to connect call", "Call Error");
      }
    } else {
      toastr.error(
        "Failed to initialize Twilio. Please try again.",
        "Call failed",
      );
    }
  }

  getIncomingCallParticipantInfo(call) {
    const callerProperties = {};

    // Guard against null/undefined call
    if (!call) {
      return callerProperties;
    }

    const customParameters = call.customParameters || {};
    const { Params, zendeskId } = customParameters;

    if (Params) {
      const searchParams = new URLSearchParams(`?${Params}`);
      for (let [prop, value] of searchParams) {
        callerProperties[prop] = value;
      }
    }
    if (zendeskId) {
      callerProperties.zendeskUrl =
        TRAVELASSIST_ZENDESK_TICKET_ENDPOINT(zendeskId);
    }
    return callerProperties;
  }

  sendDigits(call, digit) {
    if (call) {
      call.sendDigits(digit);
    }
  }

  setConnectionListeners(call, clientIsCaller) {
    _callRinging.next({ connection: call, isIncoming: !clientIsCaller });

    call.on("accept", () => {
      _callRingingStopped.next({
        connection: call,
        accepted: true,
        isIncoming: !clientIsCaller,
      });
      _callStarted.next({ connection: call, isIncoming: !clientIsCaller });
    });

    call.on("cancel", () => {
      _callRingingStopped.next({
        connection: call,
        accepted: false,
        isIncoming: !clientIsCaller,
      });
    });

    call.on("reject", () => {
      _callRingingStopped.next({
        connection: call,
        accepted: false,
        isIncoming: !clientIsCaller,
      });
    });

    call.on("disconnect", () => {
      _callStopped.next(call);
    });
  }

  async sendSMS(phoneNumber, message, applicantInfo) {
    try {
      const response = await sendTwilioSMS(phoneNumber, message, applicantInfo);
      return response.data;
    } catch (error) {
      const errorCode = error.response?.data?.error?.code;
      switch (errorCode) {
        case SMS_NOT_VALID_NUMBER_ERROR:
          toastr.error(
            `Phone number: ${phoneNumber} is not valid.`,
            "Error 21211",
          );
          break;
        case SMS_NOT_ENABLED_REGION_ERROR:
          toastr.error(
            `Permission to send an SMS has not been enabled for the region indicated by the Phone number: ${phoneNumber}`,
            "Error 21408",
          );
          break;
        default:
          toastr.error("Failed to send SMS. Please try again.", "SMS Error");
          break;
      }
      throw error;
    }
  }

  async sendWhatsApp(phoneNumber, message, applicantInfo) {
    try {
      const response = await sendTwilioWhatsApp(phoneNumber, message, applicantInfo);
      return response.data;
    } catch (error) {
      const errorCode = String(error.response?.data?.error?.code || "");
      switch (errorCode) {
        case WHATSAPP_SESSION_EXPIRED_ERROR:
          toastr.error(
            "WhatsApp 24-hour session has expired. The customer must send a message first before you can reply.",
            "Session Expired",
          );
          break;
        case WHATSAPP_NOT_VALID_NUMBER_ERROR:
          toastr.error(
            `Phone number: ${phoneNumber} is not valid.`,
            "Error 21211",
          );
          break;
        default:
          toastr.error(
            "Failed to send WhatsApp message. Please try again.",
            "WhatsApp Error",
          );
          break;
      }
      throw error;
    }
  }

  /**
   * @returns {'registered'|'registering'|'unregistered'|'destroyed'|null}
   */
  get deviceStatus() {
    try {
      return this.device ? this.device.state : null;
    } catch (err) {
      // in case Device isn't setup
      return null;
    }
  }
}

export default new TwilioService();
